import { useMemo, useState } from 'react'
import {
  Stack, TextInput, Select, Autocomplete, Button, Text, Box, Alert, Group, Divider,
} from '@mantine/core'
import {
  IconAlertCircle, IconBriefcase, IconBuildingSkyscraper, IconHash, IconMapPin,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PhoneInput } from 'react-international-phone'
import 'react-international-phone/style.css'
import { profileApi } from '@/api/profile'
import { organizationsApi } from '@/api/organizations'
import { precisaEscolherOrganizacao } from '@/components/organizations/precisaEscolherOrganizacao'
import { useAuthStore } from '@/store/auth'
import { notifications } from '@mantine/notifications'
import { JOB_TITLES_TECH } from '@/constants/jobTitles'
import { BR_STATES } from '@/constants/brStates'

interface Props {
  missingFields: string[]
  onComplete: () => void
}

const inputStyles = {
  label: { color: '#b6c2c9', fontWeight: 600, fontSize: 12, marginBottom: 4 },
  input: {
    backgroundColor: 'var(--ai-auth-input-bg)',
    borderColor: 'var(--ai-auth-border)',
    color: '#e9ecef',
  },
}

function maskCep(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 5) return digits
  return `${digits.slice(0, 5)}-${digits.slice(5)}`
}

function composeAddress(parts: {
  logradouro: string; numero: string; cep: string; cidade: string; estado: string;
}): string {
  const street = `${parts.logradouro.trim()}, ${parts.numero.trim()}`.trim()
  const cep = parts.cep.trim() ? `CEP ${parts.cep.trim()}` : ''
  const city = [parts.cidade.trim(), parts.estado.trim()].filter(Boolean).join('/')
  return [street, cep, city].filter(Boolean).join(' - ')
}

export function SetupWizardStepPersonalData({ missingFields, onComplete }: Props) {
  const qc = useQueryClient()
  const { refreshUser, logout } = useAuthStore()

  const [phone, setPhone] = useState('+55')
  const [employeeNumber, setEmployeeNumber] = useState('')
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [jobTitle, setJobTitle] = useState('')
  const [logradouro, setLogradouro] = useState('')
  const [numero, setNumero] = useState('')
  const [cep, setCep] = useState('')
  const [cidade, setCidade] = useState('')
  const [estado, setEstado] = useState<string | null>(null)
  const [error, setError] = useState('')

  // Fonte da lista: a MESMA da tela de solicitação (todas as organizações). A
  // lista escopada por tenant (`organizations/lite`) podia esconder justamente a
  // organização do usuário — o problema que este campo existe para resolver.
  //
  // Sem `enabled` por missingFields: `organization_id` saiu dos obrigatórios,
  // então derivar a query dele deixaria o campo sem opções.
  const statusQuery = useQuery({
    queryKey: ['my-organization-status'],
    queryFn: organizationsApi.myOrganizationStatus,
  })
  const orgStatus = statusQuery.data
  const pendingOrgName = orgStatus?.pending_request?.to_org_name ?? null
  // Só oferece pedido a quem cabe: já ter vínculo ou pedido aberto elimina na
  // origem os 409 `user_already_in_org` e `pending_request_exists`. Quem o
  // servidor isentou de org (auditor_global) também não vê o campo.
  const showOrgField = !!orgStatus && precisaEscolherOrganizacao(orgStatus) && !orgStatus.pending_request

  const addressFilled = useMemo(
    () => logradouro.trim() && numero.trim() && cep.replace(/\D/g, '').length === 8
      && cidade.trim() && !!estado,
    [logradouro, numero, cep, cidade, estado],
  )

  const updateMutation = useMutation({
    mutationFn: () => {
      const payload: any = {}
      if (missingFields.includes('phone')) {
        payload.phone = phone.trim()
      }
      if (missingFields.includes('employee_number')) {
        payload.employee_number = employeeNumber.trim()
      }
      // `organization_id` NÃO vai no perfil: vínculo a Organização não se
      // define por autoatendimento. A Fatia 2 usa `organizationId` para criar
      // uma solicitação de entrada, que o responsável aprova.
      if (missingFields.includes('job_title')) {
        payload.job_title = jobTitle.trim()
      }
      if (missingFields.includes('address')) {
        payload.address = composeAddress({ logradouro, numero, cep, cidade, estado: estado ?? '' })
      }
      return profileApi.update(payload)
    },
    onSuccess: async () => {
      // O pedido de entrada é ACESSÓRIO: os dados pessoais já foram salvos, então
      // uma falha aqui não pode travar o passo — o usuário pode solicitar na tela
      // seguinte. Travar transformaria falha acessória em bloqueio.
      if (organizationId) {
        try {
          await organizationsApi.requestJoinOrganization({
            to_org_id: parseInt(organizationId, 10),
          })
          notifications.show({
            color: 'teal', title: 'Solicitação enviada',
            message: 'O responsável pela organização vai aprovar seu acesso.',
          })
        } catch (err: any) {
          const detail = err?.response?.data?.detail
          // Já existe pedido aberto, ou o usuário já tem vínculo: nada a fazer.
          if (detail !== 'pending_request_exists' && detail !== 'user_already_in_org') {
            notifications.show({
              color: 'yellow', title: 'Dados salvos',
              message: 'Não foi possível enviar a solicitação de organização. '
                + 'Você pode solicitar na próxima tela.',
            })
          }
        }
      }
      notifications.show({ color: 'teal', title: 'Dados salvos', message: 'Continuando...' })
      qc.invalidateQueries({ queryKey: ['me'] })
      await refreshUser()
      onComplete()
    },
    onError: () => {
      setError('Erro ao salvar. Tente novamente.')
    },
  })

  const allFilled = missingFields.every((f) => {
    if (f === 'phone') return phone.replace(/\D/g, '').length >= 10
    if (f === 'employee_number') return employeeNumber.trim().length > 0
    if (f === 'job_title') return jobTitle.trim().length > 0
    if (f === 'address') return addressFilled
    return true
  })

  return (
    <Stack gap="md">
      <Box>
        <Text fw={700} size="md" c="#e9ecef">Complete seus dados pessoais</Text>
        <Text size="xs" c="#6c757d" mt={2}>
          Esses dados são obrigatórios para continuar.
        </Text>
      </Box>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" radius="sm"
          styles={{ root: { backgroundColor: 'rgba(255, 91, 87, 0.1)', border: '1px solid rgba(255, 91, 87, 0.2)' } }}
        >
          {error}
        </Alert>
      )}

      {missingFields.includes('phone') && (
        <Box>
          <Text component="label" size="xs" fw={600} c="#b6c2c9"
            style={{ display: 'block', marginBottom: 4 }}>
            Telefone
          </Text>
          <PhoneInput
            defaultCountry="br"
            value={phone}
            onChange={(v) => setPhone(v)}
            inputStyle={{
              backgroundColor: 'var(--ai-auth-input-bg)',
              border: '1px solid var(--ai-auth-border)',
              color: '#e9ecef',
              height: 42,
              fontSize: 14,
              flex: 1,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
            countrySelectorStyleProps={{
              buttonStyle: {
                backgroundColor: 'var(--ai-auth-input-bg)',
                border: '1px solid var(--ai-auth-border)',
                borderRight: 'none',
                height: 42,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              },
              dropdownStyleProps: {
                style: {
                  backgroundColor: 'var(--ai-auth-card-bg)',
                  border: '1px solid var(--ai-auth-border)',
                  color: '#e9ecef',
                },
              },
            }}
            inputProps={{ 'aria-label': 'Telefone' }}
          />
        </Box>
      )}

      {missingFields.includes('employee_number') && (
        <TextInput required label="Matrícula" leftSection={<IconHash size={16} />}
          value={employeeNumber} onChange={(e) => setEmployeeNumber(e.currentTarget.value)} maxLength={50}
          styles={inputStyles} size="md"
        />
      )}

      {pendingOrgName && (
        <Alert icon={<IconBuildingSkyscraper size={16} />} color="teal" radius="sm"
          styles={{ root: {
            backgroundColor: 'rgba(9, 141, 147, 0.08)',
            border: '1px solid rgba(9, 141, 147, 0.2)',
          } }}
        >
          Solicitação para <strong>{pendingOrgName}</strong> aguardando aprovação do responsável.
        </Alert>
      )}
      {/* Opcional e SEM gate por missingFields: `organization_id` saiu dos campos
          obrigatórios, então derivar a renderização dele faria o campo
          desaparecer da tela — o oposto da intenção. */}
      {showOrgField && (
        <Select label="Organização" leftSection={<IconBuildingSkyscraper size={16} />}
          description="Opcional. O responsável pela organização precisa aprovar sua entrada — você pode deixar em branco e solicitar depois."
          value={organizationId} onChange={setOrganizationId}
          data={(orgStatus?.organizations ?? []).map((o) => ({ value: String(o.id), label: o.name }))}
          placeholder={statusQuery.isLoading ? 'Carregando...' : 'Selecionar organização (opcional)'}
          searchable clearable
          styles={inputStyles} size="md"
        />
      )}

      {missingFields.includes('job_title') && (
        <Autocomplete required label="Cargo" leftSection={<IconBriefcase size={16} />}
          placeholder="Comece a digitar ou escolha da lista"
          data={[...JOB_TITLES_TECH]}
          value={jobTitle} onChange={setJobTitle} maxLength={100}
          styles={inputStyles} size="md"
        />
      )}

      {missingFields.includes('address') && (
        <Box>
          <Group gap="xs" mb={6}>
            <IconMapPin size={14} color="#6c757d" />
            <Text size="xs" fw={600} c="#b6c2c9" tt="uppercase" style={{ letterSpacing: '0.06em' }}>
              Endereço
            </Text>
          </Group>
          <Divider color="#3a4148" mb="sm" />
          <Stack gap="sm">
            <TextInput required label="Logradouro" placeholder="Av. Brasil, Rua das Flores..."
              value={logradouro} onChange={(e) => setLogradouro(e.currentTarget.value)} maxLength={150}
              styles={inputStyles} size="md"
            />
            <Group grow gap="sm">
              <TextInput required label="Número" placeholder="123"
                value={numero} onChange={(e) => setNumero(e.currentTarget.value)} maxLength={10}
                styles={inputStyles} size="md"
              />
              <TextInput required label="CEP" placeholder="00000-000"
                value={cep} onChange={(e) => setCep(maskCep(e.currentTarget.value))}
                styles={inputStyles} size="md"
              />
            </Group>
            <Group grow gap="sm" align="flex-end">
              <TextInput required label="Cidade" placeholder="Manaus"
                value={cidade} onChange={(e) => setCidade(e.currentTarget.value)} maxLength={80}
                styles={inputStyles} size="md"
              />
              <Select required label="Estado" placeholder="UF"
                value={estado} onChange={setEstado}
                data={[...BR_STATES]}
                searchable
                styles={inputStyles} size="md"
                maw={120}
              />
            </Group>
          </Stack>
        </Box>
      )}

      <Button
        type="button"
        fullWidth
        disabled={!allFilled}
        loading={updateMutation.isPending}
        onClick={() => updateMutation.mutate()}
        mt={8}
        size="md"
        radius="sm"
        style={{
          backgroundColor: allFilled ? 'var(--ai-brand-verde)' : undefined,
          border: 'none',
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        Continuar
      </Button>

      <Button
        type="button" variant="subtle" size="xs" color="gray"
        onClick={() => { void logout() }}
      >
        Sair
      </Button>
    </Stack>
  )
}
